/* ============================================================
   Concrete engine — rate cards → a priced, costed, timed quote
   (docs/SERVICE_SWITCHER_PLAN.md §2, §5.2)

   One Concrete service, five JOB TYPES on a quote: lift (foam lifting &
   leveling), coat (polyaspartic floor coating), clean (pressure washing),
   seal (penetrating sealer), caulk (joints & cracks). A concrete quote is a
   list of BLOCKS, one per job type the customer needs, each measured in its
   own terms: square feet, how far it dropped, slab sections, cracks to fill,
   pits to patch, the finish, cove base, stairs. Each job type has ONE RATE
   CARD (Catalog → Rate cards), seeded from the prices Rise & Shine publishes
   (concrete-website lib/pricing.ts PRICES).

   PRICE — what the customer pays — comes from the card's rate × the area
   (lifting's rate slides across the published range by the drop; the
   minimum once per visit; caulking's minimum only alone; cleaning the site's
   own formula with the combo discount), or, when a card says so, cost ×
   markup.

   COST, SUPPLIES and TIME come from the card's RECIPE: components, each
   scaled by what the block measures —
       per job / visit · sq ft · 100 sq ft · ft · 100 ft · sq ft-inch (foam) ·
       hole (lifting drills one every N sq ft) · section · crack-ft · pit ·
       stair · cove-ft
   A material or consumable component yields a supply line: quantity, unit
   cost, the job's cost, and how many PACKS to order (a 100 lb foam set, a
   5 gal pail, a 50 lb bag). A labor component yields crew-hours at the
   crew's loaded rate (the fence engine's rule). An equipment component is a
   per-visit or per-unit cost that is not ordered. A component can apply only
   WHEN the block says so (moisture mitigation, cracked or old-coating prep).
   The coating card's finishes each carry their own layers (base, flake,
   topcoat) and cure times.

   The result is the FENCE ENGINE'S SHAPE (lib/engine.ts computeQuote): the
   quote page, the hub, the signed freeze, invoices, reports and the work
   order's supply list read the same keys — `mat` is the supply list
   (label / qty / unit cost / total / sku / supplier, plus packs), LF/panels/
   posts are 0, and the concrete scope, time and profit ride under
   `concrete`. computeQuoteView (quote.tsx) hands a concrete quote to
   concreteViewHead() and runs its shared tail over the lines this builds.

   A card without a recipe (one saved by the first release) still prices:
   componentsOf() synthesises its old flat assumptions into components.
   Plain functions + a bottom export: scripts/unit-concrete.mjs runs them
   without the DOM.
   ============================================================ */

type JobType = "lift" | "coat" | "clean" | "seal" | "caulk";
type Per = "job" | "visit" | "sqft" | "100sqft" | "floor-sqft" | "ft" | "100ft" | "sqft-inch" | "hole" | "section" | "crack-ft" | "pit" | "stair" | "cove-ft";
type When = "" | "moisture" | "cracked" | "old-coating";
type Kind = "material" | "consumable" | "labor" | "equipment";

const JOB_TYPES: JobType[] = ["lift", "coat", "clean", "seal", "caulk"];
/* the customer-facing names, as the concrete site says them */
const JOB_LABELS: Record<JobType, string> = {
  lift: "Concrete lifting & leveling",
  coat: "Garage & floor coating",
  clean: "Concrete cleaning",
  seal: "Concrete sealing",
  caulk: "Crack & joint caulking",
};
const SURFACES: { id: string; label: string }[] = [
  { id: "driveway", label: "Driveway" }, { id: "apron", label: "Garage apron" }, { id: "walk", label: "Sidewalk / walkway" },
  { id: "patio", label: "Patio" }, { id: "pool-deck", label: "Pool deck" }, { id: "steps", label: "Steps / stoop" },
  { id: "porch", label: "Porch" }, { id: "garage-floor", label: "Garage / shop floor" }, { id: "basement", label: "Basement floor" },
  { id: "ac-pad", label: "AC pad" }, { id: "other", label: "Other slab" },
];
/* how far it dropped → where in the low–high range the rate sits (t), and the
   inches the foam estimate uses */
const DROPS: { id: string; label: string; t: number; inches: number }[] = [
  { id: "under-1", label: "Under 1 inch", t: 0, inches: 0.75 },
  { id: "1-2", label: "1 to 2 inches", t: 0.3, inches: 1.5 },
  { id: "2-3", label: "2 to 3 inches", t: 0.6, inches: 2.5 },
  { id: "3-4", label: "3 to 4 inches", t: 0.85, inches: 3.5 },
  { id: "4-plus", label: "More than 4 inches", t: 1, inches: 4.5 },
  { id: "unsure", label: "Not sure yet", t: 0.5, inches: 1.5 },
];
const CONDITIONS: { id: string; label: string }[] = [
  { id: "good", label: "Bare concrete, decent shape" },
  { id: "cracked", label: "Cracks, pits or salt damage" },
  { id: "old-coating", label: "Old coating or paint on it" },
];
const COAT_SYSTEMS: { id: string; name: string }[] = [
  { id: "full-flake", name: "Full flake" }, { id: "partial-flake", name: "Partial flake" }, { id: "solid", name: "Solid color" },
];
/* what a component scales with, in the words the card editor shows */
const PERS: { id: Per; label: string; jobTypes: JobType[] | null }[] = [
  { id: "visit", label: "per visit", jobTypes: null },
  { id: "job", label: "per job", jobTypes: null },
  { id: "sqft", label: "per sq ft", jobTypes: ["lift", "coat", "clean", "seal"] },
  { id: "100sqft", label: "per 100 sq ft", jobTypes: ["lift", "coat", "clean", "seal"] },
  { id: "floor-sqft", label: "per sq ft of floor (not the stem wall)", jobTypes: ["coat"] },
  { id: "ft", label: "per ft", jobTypes: ["caulk"] },
  { id: "100ft", label: "per 100 ft", jobTypes: ["caulk"] },
  { id: "sqft-inch", label: "per sq ft × inch of lift", jobTypes: ["lift"] },
  { id: "hole", label: "per injection hole", jobTypes: ["lift"] },
  { id: "section", label: "per slab section", jobTypes: ["lift"] },
  { id: "crack-ft", label: "per ft of cracks", jobTypes: ["lift", "coat"] },
  { id: "pit", label: "per pit / spall", jobTypes: ["coat"] },
  { id: "stair", label: "per stair tread", jobTypes: ["coat"] },
  { id: "cove-ft", label: "per ft of cove base", jobTypes: ["coat"] },
];
const KINDS: { id: Kind; label: string }[] = [
  { id: "material", label: "Material" }, { id: "consumable", label: "Consumable" }, { id: "labor", label: "Labor" }, { id: "equipment", label: "Equipment" },
];
const WHENS: { id: When; label: string }[] = [
  { id: "", label: "always" }, { id: "cracked", label: "cracks, pits or salt damage" }, { id: "old-coating", label: "old coating or paint" }, { id: "moisture", label: "moisture mitigation" },
];

interface Pack { size: number; unit: string; cost?: number | null }
interface Component {
  id?: string;
  kind: Kind;
  desc: string;                 // "Polyurethane lifting foam, 2-part set"
  per: Per;                     // what the quantity scales with
  qty: number;                  // units of this per one `per`
  unit: string;                 // lb · gal · ea · ft · hr · day
  cost: number;                 // $ per unit (labor: ignored — the crew's loaded rate)
  pack?: Pack | null;           // how it's bought: { size: 100, unit: "lb set", cost: 310 }
  sku?: string; supplier?: string;
  waste?: number;               // % added on top of what the job consumes
  when?: When;                  // only when the block says so
}
interface CoatSystem { id: string; name: string; rate: number; materialPerSqFt?: number; components?: Component[]; cure?: { walkHours?: number; parkHours?: number } }
interface RateCard {
  jobType: JobType;
  name: string;                 // the line's label on the quote (defaults to JOB_LABELS)
  description?: string;         // the one-line description under the line (lib/quotedetails.ts)
  warranty?: string;            // the warranty line printed under this job type on the quote and in the contract (plan §12 h)
  warrantyYears?: number;       // how long it runs — dated onto the job at completion (lib/concrete.actuals.ts)
  unit: "sq ft" | "ft";
  /* customer pricing */
  pricing?: "rate" | "costplus" | "lb" | "cuft";   // rate × area (published prices) or cost × markup; lift only: per lb of foam, per cu ft of void (§13 item 28)
  markup?: number;                 // costplus only; falls back to the workspace markup
  ratePerLb?: number;              // lift, pricing "lb": the customer's $ per pound of foam — estimated from the recipe, final by the lift log
  ratePerCuFt?: number;            // lift, pricing "cuft": the customer's $ per cubic foot of void (area × lift ÷ 12)
  minimum: number;              // job minimum ($); lift: once per visit; caulk: only when alone
  rateLow: number; rateHigh: number; rateDefault: number;   // $ per unit — the published range and the usual
  breakAt?: number; rateOver?: number; comboOff?: number;  // clean: the rate past breakAt, and $ off with a lift/caulk
  systems?: CoatSystem[];                                   // coat: the finishes, each its own $/sq ft and layers
  prep?: { cracked: number; oldCoating: number };           // coat: $/sq ft added to the PRICE by condition
  options?: { covePerFt: number; stairPerTread: number; moisturePerSqFt: number };   // coat: $ added to the price
  /* the recipe — cost, supplies and time */
  components?: Component[];
  sqftPerHole?: number;         // lift: one injection hole every N sq ft (default 8)
  hoursPerDay?: number;         // how a crew-day is counted (default 8)
  cure?: { walkHours?: number; parkHours?: number; driveHours?: number };
  /* legacy flat assumptions (the first release's cards; componentsOf() turns them into components) */
  materialPerUnit?: number; laborHoursPer100?: number; setupHours?: number;
  foamLbPerSqFtInch?: number; foamCostPerLb?: number; hoursPerSection?: number;
  updatedAt?: number;
}

interface Block {
  jobType: JobType;
  sqft?: number;                // lift / coat / clean / seal
  ft?: number;                  // caulk
  sections?: number;            // lift: slab sections (default from sq ft)
  drop?: string;                // lift: DROPS id
  surfaces?: string[];          // lift / clean / seal: SURFACES ids
  crackFt?: number;             // lift / coat: cracks to fill
  pits?: number;                // coat: pits / spalls to patch
  system?: string;              // coat: COAT_SYSTEMS id
  condition?: string;           // coat: CONDITIONS id
  coveFt?: number; stairs?: number; moisture?: boolean;   // coat options
  stemLf?: number; stemIn?: number;   // coat: the stem wall coated up from the floor — its length (ft) and height (in); part of the coated area, never of the flake (lib/jobcost.ts)
  floor?: string;               // coat: the floor's kind (lib/jobcost.ts FLOORS) — picks the published range the sheet shows
  flake?: string; flakeName?: string; finish?: string;     // coat: the blend and sheen the customer picked (lib/flakes.ts; the name rides along so this file stays import-free)
  fromScan?: boolean;           // filled from the photo read (lib/concrete.scan.ts)
  rate?: number | null;         // a per-unit rate typed for this quote (wins over the card)
}
interface SupplyLine {
  key: string; label: string; qty: number; uom: string; unit: number; total: number; sub: string;
  sku: string | null; supplier: string | null; packs: number | null; packLabel: string | null; packCost: number | null; kind: Kind;
}
interface LaborLine { label: string; hours: number; cost: number }
/* a lift priced by a quantity other than the area: how much of what, at what rate (§13 item 28) */
interface LiftBasis { method: "lb" | "cuft"; qty: number; unit: string; rate: number }
interface PricedBlock {
  key: string; jobType: JobType; label: string; where: string | null;
  size: number; unit: string; rate: number; byArea: number; amount: number;
  low: number; high: number; minimumApplied: boolean; comboOff: number; needsRate: boolean; missingCard: boolean;
  material: number; laborHours: number; laborCost: number; equipmentCost: number; cost: number; profit: number; marginPct: number;
  supplies: SupplyLine[]; labor: LaborLine[]; equipment: SupplyLine[];
  time: { crewHours: number; days: number; walkHours: number | null; parkHours: number | null; driveHours: number | null };
  sub: string;
  basis?: LiftBasis;            // lift priced per lb of foam or per cu ft of void; absent when priced per sq ft or cost-plus
}

const round2 = (n: any) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v: any, d = 0) => { const n = Number(v); return isFinite(n) ? n : d; };
const money = (n: any) => "$" + round2(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money0 = (n: any) => "$" + Math.round(num(n)).toLocaleString("en-US");
const fmtQty = (n: number) => (Math.abs(n - Math.round(n)) < 0.05 ? Math.round(n) : Math.round(n * 10) / 10).toLocaleString("en-US");

/* ---- the seed: Rise & Shine's published prices + a starting recipe ----
   The prices are the site's. The recipe — what a lift or a floor takes, and
   what it costs — is a starting point written to be edited: swap in your
   products, pack sizes, SKUs and suppliers on the card. */
const comp = (kind: Kind, desc: string, per: Per, qty: number, unit: string, cost: number, extra: Partial<Component> = {}): Component => ({ kind, desc, per, qty, unit, cost, ...extra });
function seedRateCards(): Record<JobType, RateCard> {
  return {
    lift: {
      jobType: "lift", name: JOB_LABELS.lift, unit: "sq ft",
      description: "Polyurethane foam injected through ⅝″ holes lifts the slab back to grade; the holes are filled flush with non-shrink grout.",
      pricing: "rate", minimum: 450, rateLow: 5.5, rateHigh: 8, rateDefault: 6.5,
      sqftPerHole: 8, hoursPerDay: 8, cure: { driveHours: 0 },
      components: [
        comp("material", "Polyurethane lifting foam, 2-part set", "sqft-inch", 0.3, "lb", 3, { pack: { size: 100, unit: "lb set", cost: 310 }, waste: 10 }),
        comp("consumable", "Injection ports", "hole", 1, "ea", 0.6, { pack: { size: 100, unit: "ct bag" } }),
        comp("consumable", "Non-shrink grout, hole patch", "hole", 0.25, "lb", 0.9, { pack: { size: 50, unit: "lb bag" } }),
        comp("consumable", "⅝″ SDS drill bits", "job", 0.25, "ea", 18),
        comp("material", "Self-leveling joint sealant", "crack-ft", 0.05, "tube", 14, { pack: { size: 12, unit: "tube case" } }),
        comp("labor", "Setup, walk & mark", "visit", 1, "hr", 0),
        comp("labor", "Drill, inject & lift", "section", 0.75, "hr", 0),
        comp("labor", "Patch holes", "hole", 0.05, "hr", 0),
        comp("labor", "Caulk cracks", "crack-ft", 0.03, "hr", 0),
        comp("equipment", "Foam rig & generator", "visit", 1, "day", 0),
      ],
    },
    coat: {
      jobType: "coat", name: JOB_LABELS.coat, unit: "sq ft",
      description: "Diamond-ground prep, repairs, then a polyaspartic system; walk on it in hours.",
      pricing: "rate", minimum: 0, rateLow: 0, rateHigh: 0, rateDefault: 0,   // the site publishes no coating price yet — set yours on the card
      hoursPerDay: 8, cure: { walkHours: 6, parkHours: 24 },
      systems: [
        // the full-flake layers carry the Simiron cart's numbers (lib/jobcost.ts — the job cost sheet):
        // a 1.5 gal base kit at $110 covering about 130 sq ft a gallon, flake at 16.7 lb per 100 sq ft
        // of FLOOR from a 40 lb box, a 10 gal topcoat kit at $990 covering 100 sq ft a gallon over flake
        { id: "full-flake", name: "Full flake", rate: 0, cure: { walkHours: 6, parkHours: 24 }, components: [
          comp("material", "Polyurea base coat", "100sqft", 0.77, "gal", 73.33, { pack: { size: 1.5, unit: "gal kit", cost: 110 }, waste: 10 }),
          comp("material", "Vinyl flake, full broadcast", "floor-sqft", 0.167, "lb", 2.62, { pack: { size: 40, unit: "lb box", cost: 104.8 }, waste: 10 }),
          comp("material", "Polyaspartic topcoat", "100sqft", 1, "gal", 99, { pack: { size: 10, unit: "gal kit", cost: 990 }, waste: 10 }),
          comp("labor", "Base coat", "100sqft", 0.35, "hr", 0),
          comp("labor", "Broadcast flake", "100sqft", 0.35, "hr", 0),
          comp("labor", "Scrape & topcoat", "100sqft", 0.5, "hr", 0),
        ] },
        { id: "partial-flake", name: "Partial flake", rate: 0, cure: { walkHours: 6, parkHours: 24 }, components: [
          comp("material", "Polyurea base coat, pigmented", "100sqft", 0.77, "gal", 73.33, { pack: { size: 1.5, unit: "gal kit", cost: 110 }, waste: 10 }),
          comp("material", "Vinyl flake, light scatter", "floor-sqft", 0.05, "lb", 2.62, { pack: { size: 40, unit: "lb box", cost: 104.8 }, waste: 10 }),
          comp("material", "Polyaspartic topcoat", "100sqft", 0.67, "gal", 99, { pack: { size: 10, unit: "gal kit", cost: 990 }, waste: 10 }),
          comp("labor", "Base coat", "100sqft", 0.35, "hr", 0),
          comp("labor", "Scatter flake", "100sqft", 0.2, "hr", 0),
          comp("labor", "Topcoat", "100sqft", 0.4, "hr", 0),
        ] },
        { id: "solid", name: "Solid color", rate: 0, cure: { walkHours: 6, parkHours: 24 }, components: [
          comp("material", "Polyurea base coat, pigmented", "100sqft", 0.77, "gal", 73.33, { pack: { size: 1.5, unit: "gal kit", cost: 110 }, waste: 10 }),
          comp("material", "Polyaspartic topcoat", "100sqft", 0.67, "gal", 99, { pack: { size: 10, unit: "gal kit", cost: 990 }, waste: 10 }),
          comp("labor", "Base coat", "100sqft", 0.35, "hr", 0),
          comp("labor", "Topcoat", "100sqft", 0.4, "hr", 0),
        ] },
      ],
      prep: { cracked: 0, oldCoating: 0 }, options: { covePerFt: 0, stairPerTread: 0, moisturePerSqFt: 0 },
      components: [
        // what every coating job throws away or wears down (the supply list we priced; rough placeholders — put in what you pay)
        comp("consumable", "Diamond grinding segments & dust bags", "100sqft", 1, "set", 15),
        comp("consumable", "Roller covers, mini rollers & chip brushes", "job", 1, "set", 75),
        comp("consumable", "Bucket liners, mixing paddles, gloves, tape & plastic", "job", 1, "set", 72),
        comp("consumable", "Filler nozzles, rags, solvent & trash bags", "job", 1, "set", 37),
        comp("consumable", "Squeegee & scraper blades, respirator cartridges, vac bags — a job's share", "job", 1, "set", 45),
        comp("material", "Crack filler, polyurea", "crack-ft", 1, "ft", 0.9, { pack: { size: 22, unit: "oz cartridge", cost: 24 } }),
        comp("material", "Pit & spall repair mortar", "pit", 1, "ea", 6),
        comp("material", "Moisture-mitigating primer", "100sqft", 0.4, "gal", 110, { pack: { size: 1, unit: "gal kit" }, when: "moisture" }),
        comp("consumable", "Coating stripper & extra grinding", "100sqft", 1, "set", 9, { when: "old-coating" }),
        comp("material", "Cove base, epoxy mortar", "cove-ft", 1, "ft", 1.5),
        comp("labor", "Setup, mask & clear", "visit", 3, "hr", 0),
        comp("labor", "Cleanup & load out", "visit", 3, "hr", 0),
        comp("labor", "Diamond grind", "100sqft", 1, "hr", 0),
        comp("labor", "Grind off the old coating", "100sqft", 0.6, "hr", 0, { when: "old-coating" }),
        comp("labor", "Crack repair", "crack-ft", 0.03, "hr", 0),
        comp("labor", "Pit & spall repair", "pit", 0.15, "hr", 0),
        comp("labor", "Moisture primer", "100sqft", 0.3, "hr", 0, { when: "moisture" }),
        comp("labor", "Cove base", "cove-ft", 0.05, "hr", 0),
        comp("labor", "Stair treads", "stair", 0.4, "hr", 0),
        comp("equipment", "Grinder, vacuum & generator", "visit", 1, "day", 0),
      ],
    },
    clean: {
      jobType: "clean", name: JOB_LABELS.clean, unit: "sq ft",
      description: "Stain-by-stain pre-treatment, then a rotary surface cleaner in even passes; runoff to the lawn.",
      pricing: "rate", minimum: 350, rateLow: 0.6, rateHigh: 0.6, rateDefault: 0.6, breakAt: 1000, rateOver: 0.55, comboOff: 50,
      hoursPerDay: 8, cure: { walkHours: 0 },
      components: [
        comp("material", "Degreaser, rust remover & mildew cleaner", "100sqft", 1, "set", 3),
        comp("labor", "Setup & pre-treat stains", "visit", 0.5, "hr", 0),
        comp("labor", "Surface-clean & rinse", "100sqft", 1, "hr", 0),
        comp("equipment", "Pressure washer & surface cleaner", "visit", 1, "day", 0),
      ],
    },
    seal: {
      jobType: "seal", name: JOB_LABELS.seal, unit: "sq ft",
      description: "Penetrating silane/siloxane sealer on clean, dry concrete; low-pressure, back-rolled, no puddles.",
      pricing: "rate", minimum: 0, rateLow: 0.4, rateHigh: 0.9, rateDefault: 0.65,
      hoursPerDay: 8, cure: { walkHours: 4, driveHours: 24 },
      components: [
        comp("material", "Penetrating sealer, silane/siloxane", "100sqft", 0.5, "gal", 28, { pack: { size: 5, unit: "gal pail", cost: 135 }, waste: 5 }),
        comp("labor", "Setup & mask", "visit", 0.5, "hr", 0),
        comp("labor", "Spray & back-roll", "100sqft", 0.4, "hr", 0),
      ],
    },
    caulk: {
      jobType: "caulk", name: JOB_LABELS.caulk, unit: "ft",
      description: "Joints routed and cleaned, backer rod where deep, self-leveling sealant tooled flush.",
      pricing: "rate", minimum: 275, rateLow: 5, rateHigh: 10, rateDefault: 7,
      hoursPerDay: 8, cure: { walkHours: 2, driveHours: 24 },
      components: [
        comp("material", "Self-leveling joint sealant", "ft", 0.05, "tube", 14, { pack: { size: 12, unit: "tube case" }, waste: 10 }),
        comp("material", "Backer rod, ½″", "ft", 1, "ft", 0.12, { pack: { size: 100, unit: "ft roll" } }),
        comp("labor", "Setup", "visit", 0.5, "hr", 0),
        comp("labor", "Rout, clean, rod & seal", "100ft", 3, "hr", 0),
      ],
    },
  };
}

/* the cards this workspace has (Settings → rateCards), job type → card; a
   stored card is normalised over the seed's shape so an older card still has
   every field the engine reads. A card's own recipe wins; a card with none
   keeps the seed's recipe only when it never had legacy cost fields either. */
function rateCardsOf(settings: any): Partial<Record<JobType, RateCard>> {
  const stored = settings && settings.rateCards && typeof settings.rateCards === "object" ? settings.rateCards : {};
  const seed = seedRateCards();
  const out: any = {};
  JOB_TYPES.forEach((jt) => {
    const c = stored[jt];
    if (!c || typeof c !== "object") return;
    const base: any = seed[jt];
    const card: any = { ...base, ...c, jobType: jt, unit: base.unit };
    if (!Array.isArray(c.components)) delete card.components;   // the seed's recipe is not silently theirs; componentsOf() derives one
    if (jt === "coat") {
      const sys = Array.isArray(c.systems) && c.systems.length ? c.systems : base.systems;
      card.systems = sys.map((s: any) => {
        const seedSys = (base.systems || []).find((x: any) => x.id === s.id);
        const row: any = { id: String(s.id || ""), name: String(s.name || s.id || ""), rate: num(s.rate), cure: s.cure || (seedSys && seedSys.cure) || base.cure };
        if (Array.isArray(s.components)) row.components = s.components;
        else if (s.materialPerSqFt != null) row.materialPerSqFt = num(s.materialPerSqFt);
        else if (seedSys && Array.isArray(seedSys.components) && !Array.isArray(c.components)) row.components = seedSys.components;
        return row;
      });
      card.prep = { ...base.prep, ...(c.prep || {}) };
      card.options = { ...base.options, ...(c.options || {}) };
    }
    out[jt] = card;
  });
  return out;
}

/* a card's recipe: its own components, or the first release's flat
   assumptions turned into the same shape */
function componentsOf(card: RateCard): Component[] {
  if (Array.isArray(card.components)) return card.components.filter((c) => c && c.desc);
  const out: Component[] = [];
  if (card.jobType === "lift") {
    if (num(card.foamLbPerSqFtInch) > 0) out.push(comp("material", "Polyurethane lifting foam", "sqft-inch", num(card.foamLbPerSqFtInch), "lb", num(card.foamCostPerLb)));
    if (num(card.setupHours) > 0) out.push(comp("labor", "Setup", "visit", num(card.setupHours), "hr", 0));
    if (num(card.hoursPerSection) > 0) out.push(comp("labor", "Drill, inject & lift", "section", num(card.hoursPerSection), "hr", 0));
    if (num(card.laborHoursPer100) > 0) out.push(comp("labor", "Lifting", "100sqft", num(card.laborHoursPer100), "hr", 0));
    return out;
  }
  const per: Per = card.unit === "ft" ? "ft" : "sqft";
  const per100: Per = card.unit === "ft" ? "100ft" : "100sqft";
  if (num(card.materialPerUnit) > 0) out.push(comp("material", card.jobType === "caulk" ? "Joint sealant & backer rod" : card.jobType === "seal" ? "Penetrating sealer" : card.jobType === "clean" ? "Cleaners & stain treatments" : "Coating materials", per, 1, card.unit, num(card.materialPerUnit)));
  if (num(card.setupHours) > 0) out.push(comp("labor", "Setup", "visit", num(card.setupHours), "hr", 0));
  if (num(card.laborHoursPer100) > 0) out.push(comp("labor", JOB_LABELS[card.jobType], per100, num(card.laborHoursPer100), "hr", 0));
  return out;
}
/* a coating finish's own layers (or its flat material $/sq ft, from the first release) */
function systemComponentsOf(sys: CoatSystem | undefined): Component[] {
  if (!sys) return [];
  if (Array.isArray(sys.components)) return sys.components.filter((c) => c && c.desc);
  if (num(sys.materialPerSqFt) > 0) return [comp("material", sys.name + " coating materials", "sqft", 1, "sq ft", num(sys.materialPerSqFt))];
  return [];
}

/* what's wrong with a card, for the list and the editor */
function cardWarnings(card: RateCard | null | undefined): { sev: "bad" | "warn"; text: string }[] {
  const out: { sev: "bad" | "warn"; text: string }[] = [];
  if (!card) return [{ sev: "bad", text: "No rate card — this job type can't be priced yet" }];
  const costplus = card.pricing === "costplus";
  if (card.jobType === "coat") {
    const priced = (card.systems || []).filter((s) => num(s.rate) > 0);
    if (!costplus && !priced.length) out.push({ sev: "bad", text: "No $/sq ft on any finish — coating quotes price at $0 until you set one" });
    else if (!costplus && priced.length < (card.systems || []).length) out.push({ sev: "warn", text: `${(card.systems || []).length - priced.length} finish${(card.systems || []).length - priced.length === 1 ? " has" : "es have"} no price` });
  } else if (card.jobType === "lift" && (card.pricing === "lb" || card.pricing === "cuft")) {
    const r = card.pricing === "lb" ? num(card.ratePerLb) : num(card.ratePerCuFt);
    const what = card.pricing === "lb" ? "lb of foam" : "cu ft of void";
    if (!(r > 0)) out.push({ sev: "bad", text: `No $/${what} — lifting prices at $0 until you set one` });
    else out.push({ sev: "warn", text: `Priced per ${what} — the website publishes lifting prices only from a card priced per sq ft` });
  } else if (!costplus && !(num(card.rateDefault) > 0)) out.push({ sev: "bad", text: "No rate — this job type prices at $0 until you set one" });
  const comps = componentsOf(card);
  const mats = comps.filter((c) => c.kind === "material" || c.kind === "consumable");
  if (!mats.length && !(card.jobType === "coat" && (card.systems || []).some((s) => systemComponentsOf(s).some((c) => c.kind === "material")))) out.push({ sev: "warn", text: "No materials on the recipe — margin reads as labor only and the supply list is empty" });
  else if (mats.some((c) => !(num(c.cost) > 0))) out.push({ sev: "warn", text: `${mats.filter((c) => !(num(c.cost) > 0)).length} material line${mats.filter((c) => !(num(c.cost) > 0)).length === 1 ? " has" : "s have"} no cost` });
  if (!comps.some((c) => c.kind === "labor")) out.push({ sev: "warn", text: "No labor on the recipe — time on site reads as zero" });
  if (!(typeof card.warranty === "string" && card.warranty.trim())) out.push({ sev: "warn", text: "No warranty line — the quote and the contract print no warranty for this job type" });
  return out;
}

/* ---- blocks: what's on the quote ----
   New quotes carry inputs.concrete.blocks; the first release's typed estimate
   carried { jobTypes, surfaces, sqft, drop, caulkFt } — normalised here so an
   older quote prices the same way. */
function blocksOf(concrete: any): Block[] {
  const c = concrete && typeof concrete === "object" ? concrete : {};
  if (Array.isArray(c.blocks)) {
    return c.blocks.filter((b: any) => b && JOB_TYPES.indexOf(b.jobType) > -1).map((b: any) => ({ ...b, jobType: b.jobType as JobType }));
  }
  const jts: JobType[] = (Array.isArray(c.jobTypes) ? c.jobTypes : []).filter((j: any) => JOB_TYPES.indexOf(j) > -1);
  const surfaces = Array.isArray(c.surfaces) ? c.surfaces.slice() : [];
  return jts.map((jt) => {
    if (jt === "caulk") return { jobType: jt, ft: num(c.caulkFt) };
    if (jt === "coat") return { jobType: jt, sqft: num(c.sqft), system: "full-flake", condition: "good" };
    if (jt === "lift") return { jobType: jt, sqft: num(c.sqft), drop: c.drop || "unsure", surfaces };
    return { jobType: jt, sqft: num(c.sqft), surfaces };
  });
}

const dropOf = (id: any) => DROPS.find((d) => d.id === id) || DROPS[DROPS.length - 1];
const surfaceLabel = (id: any) => { const s = SURFACES.find((x) => x.id === id); return s ? s.label : String(id || ""); };
const whereOf = (b: Block) => (Array.isArray(b.surfaces) && b.surfaces.length ? b.surfaces.map(surfaceLabel).join(", ") : null);
/* 1.5 → "1½″", 0.75 → "¾″", 2.5 → "2½″" */
function fmtInches(n: number) {
  const whole = Math.floor(n), frac = n - whole;
  const f = frac >= 0.7 ? "¾" : frac >= 0.45 ? "½" : frac >= 0.2 ? "¼" : "";
  return (whole || !f ? String(whole) : "") + f + "″";
}
const sectionsOf = (b: Block) => Math.max(1, Math.round(num(b.sections) || Math.ceil(num(b.sqft) / 100) || 1));
const holesOf = (b: Block, card: RateCard) => (num(b.sqft) > 0 ? Math.max(1, Math.ceil(num(b.sqft) / Math.max(1, num(card.sqftPerHole, 8)))) : 0);
/* a coating's stem wall in square feet: 60 ft × 4″ = 20 sq ft. The customer
   pays for it and the coats cover it; the flake and the grinder stay on the floor. */
const stemSqftOf = (b: Block) => (b.jobType === "coat" ? round2(Math.max(0, num(b.stemLf)) * Math.max(0, num(b.stemIn)) / 12) : 0);
/* the lift rate for a drop: inside the card's range, by how far it sank */
function liftRateFor(card: RateCard, dropId: any): number {
  const lo = num(card.rateLow), hi = num(card.rateHigh);
  if (!(hi > lo)) return num(card.rateDefault) || hi || lo;
  return round2(lo + (hi - lo) * dropOf(dropId).t);
}

/* ---- how a lift is priced (§13 item 28) ----
   Per sq ft by the drop is the published way — the site shows it and the
   homeowner compares it. Per lb of foam is time-and-materials: the estimate
   comes from the recipe (foam per sq ft-inch, with its waste), the final
   amount from the lift log's pounds — for commercial work and unknown voids.
   Per cu ft of void is mudjacking's unit (area × lift ÷ 12), for the owner
   who quotes against a mudjacker. Cost × markup is the fourth. Only the sq ft
   method is published to the website (server concretequote.js). */
type LiftMethod = "rate" | "lb" | "cuft" | "costplus";
const LIFT_METHODS: { id: LiftMethod; label: string; hint: string }[] = [
  { id: "rate", label: "Per sq ft, by the drop", hint: "The rate slides across the published range by how far it sank, times the area, never under the minimum. What the website publishes and what homeowners compare." },
  { id: "lb", label: "Per lb of foam", hint: "The customer pays for the foam that goes in: estimated here from the recipe, billed on the lift log's pounds, never under the minimum. Time-and-materials for commercial work and unknown voids; the site publishes no lifting price." },
  { id: "cuft", label: "Per cu ft of void", hint: "Mudjacking's unit: the area times the lift, divided by 12, times the rate, never under the minimum. For quoting against a mudjacker; the site publishes no lifting price." },
  { id: "costplus", label: "Cost × markup", hint: "The recipe's cost (foam, crew time, the rig) times the markup, never under the minimum. The rates below become the published range only." },
];
function liftMethodOf(card: RateCard | null | undefined): LiftMethod {
  const p = card && card.pricing;
  return p === "lb" || p === "cuft" || p === "costplus" ? p : "rate";
}
/** the void a lift fills, in cubic feet: the area times the drop's inches, over 12 */
function liftVoidCuFt(sqft: number, dropId: any): number { return round2(Math.max(0, num(sqft)) * dropOf(dropId).inches / 12); }
/** the foam a lift is estimated to take, in pounds, with the recipe's waste — the same number the supply list orders against */
function liftFoamLb(comps: Component[], measures: any, b: Block): number {
  return round2((comps || []).filter((c) => c && c.kind !== "labor" && c.per === "sqft-inch" && applies(c, b)).reduce((s, c) => s + num(measures && measures["sqft-inch"]) * num(c.qty) * (1 + Math.max(0, num(c.waste)) / 100), 0));
}
/** the rate an estimate starts from, in the method's unit — the block editor's "card:" hint */
function liftUnitRate(card: RateCard, dropId: any): { rate: number; unit: string } {
  const m = liftMethodOf(card);
  if (m === "lb") return { rate: num(card.ratePerLb), unit: "lb" };
  if (m === "cuft") return { rate: num(card.ratePerCuFt), unit: "cu ft" };
  return { rate: liftRateFor(card, dropId), unit: "sq ft" };
}
function liftBasisOf(card: RateCard, b: Block, comps: Component[], measures: any): LiftBasis | null {
  const m = liftMethodOf(card);
  if (m === "lb") return { method: "lb", qty: liftFoamLb(comps, measures, b), unit: "lb", rate: num(card.ratePerLb) };
  if (m === "cuft") return { method: "cuft", qty: liftVoidCuFt(num(b.sqft), b.drop), unit: "cu ft", rate: num(card.ratePerCuFt) };
  return null;
}

/* the cleaning price exactly as the site computes it (lib/pricing.ts cleaningPrice) */
function cleaningPrice(area: number, card: RateCard, withCaulkOrLift: boolean) {
  const breakAt = num(card.breakAt, 1000), rate = num(card.rateDefault), over = num(card.rateOver, rate), min = num(card.minimum), combo = num(card.comboOff);
  const first = Math.min(area, breakAt), rest = Math.max(0, area - breakAt);
  const byArea = Math.round(first * rate + rest * over);
  const base = Math.max(min, byArea);
  return { byArea, base, price: withCaulkOrLift ? Math.max(0, base - combo) : base, minimumApplies: byArea < min, comboOff: withCaulkOrLift ? combo : 0 };
}

/* ---- the takeoff: what one block measures, and the recipe scaled to it ---- */
function measuresOf(b: Block, card: RateCard): Record<Per, number> {
  const floorSqft = Math.max(0, num(b.sqft)), ft = Math.max(0, num(b.ft));
  const sqft = floorSqft + stemSqftOf(b);   // a coating's area is the floor plus the stem wall
  const inches = b.jobType === "lift" ? dropOf(b.drop).inches : 0;
  return {
    job: 1, visit: 1,
    sqft, "100sqft": sqft / 100, "floor-sqft": floorSqft, ft, "100ft": ft / 100,
    "sqft-inch": sqft * inches,
    hole: b.jobType === "lift" ? holesOf(b, card) : 0,
    section: b.jobType === "lift" ? sectionsOf(b) : 0,
    "crack-ft": Math.max(0, num(b.crackFt)),
    pit: Math.max(0, Math.round(num(b.pits))),
    stair: Math.max(0, Math.round(num(b.stairs))),
    "cove-ft": Math.max(0, num(b.coveFt)),
  };
}
function applies(c: Component, b: Block): boolean {
  const w = c.when || "";
  if (!w) return true;
  if (w === "moisture") return !!b.moisture;
  return (b.condition || "good") === w;
}
function takeoff(b: Block, card: RateCard, comps: Component[], crewRate: number, keyPrefix: string) {
  const m = measuresOf(b, card);
  const supplies: SupplyLine[] = [], labor: LaborLine[] = [], equipment: SupplyLine[] = [];
  comps.forEach((c, i) => {
    if (!applies(c, b)) return;
    const base = num(m[c.per]) * num(c.qty);
    if (!(base > 0)) return;
    if (c.kind === "labor") {
      const hours = round2(base);
      labor.push({ label: c.desc, hours, cost: round2(hours * crewRate) });
      return;
    }
    const qty = round2(base * (1 + Math.max(0, num(c.waste)) / 100));
    const unitCost = Math.max(0, num(c.cost));
    const total = round2(qty * unitCost);
    const pack = c.pack && num(c.pack.size) > 0 ? c.pack : null;
    const packs = pack ? Math.max(1, Math.ceil(qty / num(pack.size))) : null;
    const packLabel = pack ? `${packs} × ${fmtQty(num(pack.size))} ${pack.unit}` : null;
    const packCost = pack && num(pack.cost) > 0 ? round2(packs! * num(pack.cost)) : null;
    const row: SupplyLine = {
      key: keyPrefix + "-" + (c.id || i), label: c.desc, qty, uom: c.unit, unit: unitCost, total,
      sub: `${fmtQty(qty)} ${c.unit}${unitCost > 0 ? ` × ${money(unitCost)}` : ""}${packLabel ? ` · order ${packLabel}` : ""}${packCost != null ? ` (${money0(packCost)})` : ""}`,
      sku: c.sku || null, supplier: c.supplier || null, packs, packLabel, packCost, kind: c.kind,
    };
    (c.kind === "equipment" ? equipment : supplies).push(row);
  });
  return { measures: m, supplies, labor, equipment,
    material: round2(supplies.reduce((s, r) => s + r.total, 0)),
    laborHours: round2(labor.reduce((s, r) => s + r.hours, 0)),
    laborCost: round2(labor.reduce((s, r) => s + r.cost, 0)),
    equipmentCost: round2(equipment.reduce((s, r) => s + r.total, 0)) };
}

/* the crew's loaded $/hr and headcount — the fence engine's rule (lib/engine.ts crewLoadedRate) */
function crewRateOf(settings: any) {
  const s = settings || {};
  const crews = Array.isArray(s.crews) ? s.crews : [];
  const crew = crews.find((c: any) => c && c.id === s.activeCrewId) || crews[0] || null;
  if (!crew) return { crew: { name: "Crew" }, crewRate: (num(s.laborRate) || 28) * 2, headcount: 2 };
  const members = Array.isArray(crew.members) ? crew.members : [];
  const withWage = members.filter((m: any) => num(m.wage) > 0);
  const base = members.length && withWage.length
    ? members.reduce((sum: number, m: any) => sum + (num(m.wage) > 0 ? num(m.wage) : num(crew.wage)), 0)
    : num(crew.size) * num(crew.wage);
  const loaded = base * (1 + num(crew.burdenPct) / 100);
  return { crew, crewRate: loaded || ((num(crew.size) || 2) * (num(crew.wage) || 28)), headcount: members.length || num(crew.size) || 2 };
}

/* price and cost every block on a quote. ctx is the quote itself: which other
   job types ride along decide the lift minimum, the caulk minimum and the
   cleaning combo. */
function priceBlocks(blocks: Block[], cards: Partial<Record<JobType, RateCard>>, settings?: any) {
  const s = settings || {};
  const { crewRate } = crewRateOf(s);
  const list = Array.isArray(blocks) ? blocks : [];
  const has = (jt: JobType) => list.some((b) => b.jobType === jt);
  const hasLift = has("lift"), hasCaulk = has("caulk");
  const counts: any = {};
  const priced: PricedBlock[] = [];
  const missing: JobType[] = [];
  const zeroTime = { crewHours: 0, days: 0, walkHours: null, parkHours: null, driveHours: null };
  for (const b of list) {
    const jt = b.jobType;
    counts[jt] = (counts[jt] || 0) + 1;
    const key = counts[jt] > 1 ? jt + "-" + counts[jt] : jt;
    const card = cards[jt];
    const label = (card && card.name) || JOB_LABELS[jt];
    const where = whereOf(b);
    const unit = jt === "caulk" ? "ft" : "sq ft";
    const size = jt === "caulk" ? Math.max(0, num(b.ft)) : Math.max(0, num(b.sqft));
    if (!card) {
      missing.push(jt);
      priced.push({ key, jobType: jt, label, where, size, unit, rate: 0, byArea: 0, amount: 0, low: 0, high: 0, minimumApplied: false, comboOff: 0, needsRate: true, missingCard: true,
        material: 0, laborHours: 0, laborCost: 0, equipmentCost: 0, cost: 0, profit: 0, marginPct: 0, supplies: [], labor: [], equipment: [], time: zeroTime,
        sub: `${fmtQty(size)} ${unit} · no rate card yet` });
      continue;
    }
    // the recipe → supplies, hours, equipment (the coating finish adds its layers)
    const sys = jt === "coat" ? ((card.systems || []).find((x) => x.id === b.system) || (card.systems || [])[0]) : undefined;
    const comps = [...componentsOf(card), ...(jt === "coat" ? systemComponentsOf(sys) : [])];
    const t = takeoff(b, card, comps, crewRate, key);
    const cost = round2(t.material + t.laborCost + t.equipmentCost);
    const hoursPerDay = Math.max(1, num(card.hoursPerDay, 8));
    const cure = { ...(card.cure || {}), ...((sys && sys.cure) || {}) } as any;
    const time = { crewHours: t.laborHours, days: t.laborHours > 0 ? Math.max(1, Math.ceil(t.laborHours / hoursPerDay)) : 0,
      walkHours: cure.walkHours != null ? num(cure.walkHours) : null, parkHours: cure.parkHours != null ? num(cure.parkHours) : null, driveHours: cure.driveHours != null ? num(cure.driveHours) : null };
    const typed = b.rate != null && b.rate !== ("" as any) && isFinite(Number(b.rate)) && Number(b.rate) >= 0 ? round2(b.rate) : null;
    const costplus = card.pricing === "costplus";
    const markup = Math.max(1, num(card.markup) || num(s.markup) || 1.5);
    const extras: Partial<PricedBlock> = { material: t.material, laborHours: t.laborHours, laborCost: t.laborCost, equipmentCost: t.equipmentCost, cost, supplies: t.supplies, labor: t.labor, equipment: t.equipment, time };
    const finish = (p: PricedBlock) => { p.profit = round2(p.amount - p.cost); p.marginPct = p.amount > 0 ? (p.profit / p.amount) * 100 : 0; priced.push(p); };
    if (jt === "lift") {
      const sqft = size, sections = sectionsOf(b), holes = holesOf(b, card);
      const d = dropOf(b.drop);
      // how the lift is priced (§13 item 28): per sq ft by the drop, per lb of foam (estimated here,
      // final by the lift log), per cu ft of void, or cost × markup; a typed rate is in the method's unit
      const basis0 = liftBasisOf(card, b, comps, t.measures);
      const rate = typed != null ? typed : costplus && sqft > 0 ? round2(cost * markup / sqft) : basis0 ? basis0.rate : liftRateFor(card, b.drop);
      const basis = basis0 ? { ...basis0, rate } : undefined;
      const byArea = basis ? round2(basis.qty * rate) : round2(sqft * rate);
      const basisText = basis ? (basis.method === "lb" ? ` · about ${fmtQty(basis.qty)} lb of foam at ${money(rate)}/lb — final by the lift log` : ` · about ${fmtQty(basis.qty)} cu ft of void at ${money(rate)}/cu ft`) : "";
      finish({ key, jobType: jt, label, where, size: sqft, unit, rate, byArea, amount: byArea,
        low: basis ? byArea : round2(sqft * num(card.rateLow)), high: basis ? byArea : round2(sqft * num(card.rateHigh)), minimumApplied: false, comboOff: 0,
        needsRate: !(rate > 0), missingCard: false, ...extras, ...(basis ? { basis } : {}),
        sub: `${fmtQty(sqft)} sq ft · ${sections} section${sections === 1 ? "" : "s"}${d.id === "unsure" ? "" : " · about " + fmtInches(d.inches) + " lift"}${holes ? ` · ${holes} holes` : ""}${num(b.crackFt) > 0 ? ` · ${fmtQty(num(b.crackFt))} ft of cracks caulked` : ""}${basisText}` } as PricedBlock);
    } else if (jt === "caulk") {
      const ft = size;
      const rate = typed != null ? typed : costplus && ft > 0 ? round2(cost * markup / ft) : num(card.rateDefault);
      const byArea = round2(ft * rate);
      const minAlone = hasLift ? 0 : num(card.minimum);   // caulking's minimum only when it's the whole job
      const amount = ft > 0 ? Math.max(minAlone, byArea) : 0;
      finish({ key, jobType: jt, label, where, size: ft, unit, rate, byArea, amount,
        low: Math.max(ft > 0 ? minAlone : 0, round2(ft * num(card.rateLow))), high: Math.max(ft > 0 ? minAlone : 0, round2(ft * num(card.rateHigh))),
        minimumApplied: ft > 0 && amount > byArea, comboOff: 0, needsRate: !(rate > 0), missingCard: false, ...extras,
        sub: `${fmtQty(ft)} ft of joints & cracks${ft > 0 && amount > byArea ? " · job minimum" : ""}` } as PricedBlock);
    } else if (jt === "clean") {
      const sqft = size;
      const card2 = typed != null ? { ...card, rateDefault: typed, rateOver: Math.min(typed, num(card.rateOver, typed)) } : card;
      const cp = cleaningPrice(sqft, card2, hasLift || hasCaulk);
      const amount = sqft > 0 ? (costplus && typed == null ? Math.max(num(card.minimum), round2(cost * markup)) - (hasLift || hasCaulk ? num(card.comboOff) : 0) : round2(cp.price)) : 0;
      finish({ key, jobType: jt, label, where, size: sqft, unit, rate: num(card2.rateDefault), byArea: cp.byArea, amount,
        low: amount, high: amount, minimumApplied: sqft > 0 && cp.minimumApplies, comboOff: sqft > 0 ? cp.comboOff : 0, needsRate: !(num(card2.rateDefault) > 0) && !costplus, missingCard: false, ...extras,
        sub: `${fmtQty(sqft)} sq ft${sqft > 0 && cp.minimumApplies ? " · job minimum" : ""}${sqft > 0 && cp.comboOff ? ` · ${money0(cp.comboOff)} off with the ${hasLift ? "lift" : "caulking"}` : ""}` } as PricedBlock);
    } else if (jt === "seal") {
      const sqft = size;
      const rate = typed != null ? typed : costplus && sqft > 0 ? round2(cost * markup / sqft) : num(card.rateDefault);
      const byArea = round2(sqft * rate);
      const amount = sqft > 0 ? Math.max(num(card.minimum), byArea) : 0;
      finish({ key, jobType: jt, label, where, size: sqft, unit, rate, byArea, amount,
        low: round2(sqft * num(card.rateLow)), high: round2(sqft * num(card.rateHigh)), minimumApplied: sqft > 0 && amount > byArea, comboOff: 0, needsRate: !(rate > 0), missingCard: false, ...extras,
        sub: `${fmtQty(sqft)} sq ft · penetrating sealer${sqft > 0 && amount > byArea ? " · job minimum" : ""}` } as PricedBlock);
    } else {
      // coat — priced on the coated area: the floor plus the stem wall
      const stem = stemSqftOf(b);
      const sqft = round2(size + stem);
      const systems = card.systems || [];
      const sysRow = sys || { id: "full-flake", name: "Full flake", rate: 0 };
      const cond = CONDITIONS.find((c) => c.id === b.condition) || CONDITIONS[0];
      const prep = cond.id === "cracked" ? num(card.prep && card.prep.cracked) : cond.id === "old-coating" ? num(card.prep && card.prep.oldCoating) : 0;
      const opts = card.options || { covePerFt: 0, stairPerTread: 0, moisturePerSqFt: 0 };
      const extraPrice = round2(num(b.coveFt) * num(opts.covePerFt) + num(b.stairs) * num(opts.stairPerTread) + (b.moisture ? sqft * num(opts.moisturePerSqFt) : 0));
      const rate = typed != null ? typed : costplus && sqft > 0 ? round2(Math.max(0, cost * markup - extraPrice) / sqft - prep) : num(sysRow.rate);
      const byArea = round2(sqft * (rate + prep) + extraPrice);
      const amount = sqft > 0 ? Math.max(num(card.minimum), byArea) : 0;
      const rates = systems.map((x) => num(x.rate)).filter((r) => r > 0);
      const lo = rates.length ? Math.min(...rates) : rate, hi = rates.length ? Math.max(...rates) : rate;
      const parts = [stem > 0 ? `${fmtQty(size)} sq ft floor + ${fmtQty(stem)} sq ft stem wall` : `${fmtQty(sqft)} sq ft`, sysRow.name.toLowerCase() + (b.flakeName && b.system !== "solid" ? ` in ${b.flakeName}` : ""), cond.label.toLowerCase()];
      if (num(b.crackFt) > 0) parts.push(`${fmtQty(num(b.crackFt))} ft of cracks filled`);
      if (num(b.pits) > 0) parts.push(`${Math.round(num(b.pits))} pit${Math.round(num(b.pits)) === 1 ? "" : "s"} patched`);
      if (b.moisture) parts.push("moisture mitigation");
      if (num(b.coveFt) > 0) parts.push(`${fmtQty(num(b.coveFt))} ft cove base`);
      if (num(b.stairs) > 0) parts.push(`${Math.round(num(b.stairs))} stair tread${Math.round(num(b.stairs)) === 1 ? "" : "s"}`);
      if (sqft > 0 && amount > byArea) parts.push("job minimum");
      finish({ key, jobType: jt, label, where, size: sqft, unit, rate, byArea, amount,
        low: round2(sqft * (lo + prep) + extraPrice), high: round2(sqft * (hi + prep) + extraPrice), minimumApplied: sqft > 0 && amount > byArea, comboOff: 0, needsRate: !(rate > 0), missingCard: false, ...extras,
        sub: parts.join(" · ") } as PricedBlock);   // size is the coated area; the block's own sqft stays the floor
    }
  }
  // the lift minimum applies once per visit: if every lift area together is
  // under it, the first lift line carries the difference
  const lifts = priced.filter((p) => p.jobType === "lift" && p.size > 0);
  const liftCard = cards.lift;
  if (lifts.length && liftCard) {
    const sum = lifts.reduce((s2, p) => s2 + p.amount, 0);
    const min = num(liftCard.minimum);
    if (sum < min) {
      const first = lifts[0];
      first.amount = round2(first.amount + (min - sum));
      first.minimumApplied = true;
      first.sub += " · job minimum";
      first.profit = round2(first.amount - first.cost); first.marginPct = first.amount > 0 ? (first.profit / first.amount) * 100 : 0;
    }
    const lowSum = lifts.reduce((s2, p) => s2 + p.low, 0), highSum = lifts.reduce((s2, p) => s2 + p.high, 0);
    if (lowSum < min) lifts[0].low = round2(lifts[0].low + (min - lowSum));
    if (highSum < min) lifts[0].high = round2(lifts[0].high + (min - highSum));
  }
  const subtotal = round2(priced.reduce((s2, p) => s2 + p.amount, 0));
  const crewHours = round2(priced.reduce((s2, p) => s2 + p.laborHours, 0));
  const hoursPerDay = Math.max(1, ...priced.map((p) => num(cards[p.jobType] && cards[p.jobType]!.hoursPerDay, 8)));
  const maxOf = (k: "walkHours" | "parkHours" | "driveHours") => { const v = priced.map((p) => p.time[k]).filter((x) => x != null) as number[]; return v.length ? Math.max(...v) : null; };
  return {
    priced, missing, subtotal,
    low: round2(priced.reduce((s2, p) => s2 + p.low, 0)), high: round2(priced.reduce((s2, p) => s2 + p.high, 0)),
    materialTotal: round2(priced.reduce((s2, p) => s2 + p.material, 0)),
    laborHours: crewHours,
    laborCost: round2(priced.reduce((s2, p) => s2 + p.laborCost, 0)),
    equipmentCost: round2(priced.reduce((s2, p) => s2 + p.equipmentCost, 0)),
    time: { crewHours, days: crewHours > 0 ? Math.max(1, Math.ceil(crewHours / hoursPerDay)) : 0, walkHours: maxOf("walkHours"), parkHours: maxOf("parkHours"), driveHours: maxOf("driveHours") },
    crewRate,
  };
}

/* "3.9 crew-hrs · 1 day · walk on after 6 h · park after 24 h" */
function fmtTime(time: any, headcount?: number): string {
  if (!time || !(time.crewHours > 0)) return "";
  const parts: string[] = [];
  const h = num(time.crewHours);
  parts.push(`${h < 10 ? h.toFixed(1).replace(/\.0$/, "") : Math.round(h)} crew-hr${h === 1 ? "" : "s"}`);
  if (headcount && headcount > 0) { const onSite = h / headcount; parts.push(`about ${onSite < 10 ? (Math.round(onSite * 2) / 2).toString() : Math.round(onSite)} h on site with ${headcount}`); }
  if (time.days > 0) parts.push(`${time.days} day${time.days === 1 ? "" : "s"}`);
  if (time.walkHours != null) parts.push(time.walkHours === 0 ? "walk on it right away" : `walk on after ${time.walkHours} h`);
  if (time.parkHours != null) parts.push(`park after ${time.parkHours} h`);
  else if (time.driveHours != null) parts.push(time.driveHours === 0 ? "drive on it right away" : `drive on after ${time.driveHours} h`);
  return parts.join(" · ");
}

/* the job's costs beyond the recipes — travel, overhead, the sales tax paid on
   the coatings (material lines only; consumables are priced as bought) and
   shipping. `sd` is settings.serviceDefaults.concrete; `input.miles` the
   quote's round trip. Nothing on an empty quote. */
interface JobExtras { miles: number; mileRate: number; travel: number; overhead: number; supplyTaxPct: number; supplyTax: number; shipping: number; total: number }
function jobExtrasOf(input: any, sd: any, pb: { priced: PricedBlock[] }): JobExtras {
  const d = sd && typeof sd === "object" ? sd : {};
  const miles = Math.max(0, num(input && input.miles));
  const mileRate = Math.max(0, num(d.mileRate)), supplyTaxPct = Math.max(0, num(d.supplyTaxPct));
  const live = pb.priced.some((p) => p.size > 0);
  const materials = round2(pb.priced.reduce((s2, p) => s2 + p.supplies.filter((r) => r.kind === "material").reduce((a, r) => a + r.total, 0), 0));
  const travel = live ? round2(miles * mileRate) : 0;
  const overhead = live ? round2(Math.max(0, num(d.overheadPerJob))) : 0;
  const supplyTax = live ? round2(materials * supplyTaxPct / 100) : 0;
  const shipping = live && materials > 0 ? round2(Math.max(0, num(d.shippingPerJob))) : 0;
  return { miles, mileRate, travel, overhead, supplyTaxPct, supplyTax, shipping, total: round2(travel + overhead + supplyTax + shipping) };
}

/* the quote, in the fence engine's result shape */
function computeConcreteQuote(input: any, cards: Partial<Record<JobType, RateCard>>, settings: any) {
  const s = settings || {};
  const blocks = blocksOf(input);
  const pb = priceBlocks(blocks, cards, s);
  const { crew, crewRate, headcount } = crewRateOf(s);
  const laborCost = pb.laborCost;
  const subtotal = pb.subtotal;
  // sales tax: the concrete service's own rate when Settings → Payments & tax
  // sets one (plan §12 g), else the workspace's
  const sd = s.serviceDefaults && s.serviceDefaults.concrete && typeof s.serviceDefaults.concrete === "object" ? s.serviceDefaults.concrete : {};
  const taxRate = Math.max(0, num(sd.taxRate != null && sd.taxRate !== "" ? sd.taxRate : s.taxRate));
  const tax = round2(subtotal * taxRate);
  const customerTotal = round2(subtotal + tax);
  // the job's own costs beyond the recipes (plan §13 item 22): the drive out
  // and back, the overhead a job carries, the sales tax paid on the coatings
  // and their shipping — Settings → Payments → Concrete job costs, and the
  // quote's round trip. Zero until the owner sets them, so a workspace that
  // never did costs a job at what its recipes cost, as before.
  const extras = jobExtrasOf(input, sd, pb);
  const operatorCost = round2(pb.materialTotal + laborCost + pb.equipmentCost + extras.total);
  const grossProfit = round2(subtotal - operatorCost);
  const marginPct = subtotal > 0 ? (grossProfit / subtotal) * 100 : 0;
  // the supply list, merged across blocks by label (the work order orders it,
  // the purchase order lines come from it, inventory matches it by label)
  const byLabel: Record<string, any> = {};
  const mat: any[] = [];
  pb.priced.forEach((p) => p.supplies.forEach((r) => {
    const hit = byLabel[r.label];
    if (hit) {
      hit.qty = round2(hit.qty + r.qty); hit.total = round2(hit.total + r.total);
      if (hit.packs != null && r.packs != null) {
        // the pack price follows the pack count (it used to keep the first block's — a PO for two lifts priced one)
        const perPack = hit.packs > 0 && num(hit.packCost) > 0 ? num(hit.packCost) / hit.packs : (r.packs > 0 ? num(r.packCost) / r.packs : 0);
        hit.packs = Math.ceil(hit.qty / (hit.packSize || 1)); hit.packLabel = hit.packSize ? `${hit.packs} × ${fmtQty(hit.packSize)} ${hit.packUnit}` : hit.packLabel;
        if (perPack > 0) hit.packCost = round2(hit.packs * perPack);
      }
      hit.sub = `${fmtQty(hit.qty)} ${hit.uom}${hit.unit > 0 ? ` × ${money(hit.unit)}` : ""}${hit.packLabel ? ` · order ${hit.packLabel}` : ""}`;
      return;
    }
    const packSize = r.packLabel ? parseFloat(String(r.packLabel).split("×")[1]) || 0 : 0;   // "2 × 100 lb set" → 100 (num() read the words as 0, so a merged row counted single pounds as packs)
    const packUnit = r.packLabel ? String(r.packLabel).split("×")[1].trim().replace(/^[\d.,]+\s*/, "") : "";
    const row = { ...r, packSize: packSize || null, packUnit };
    byLabel[r.label] = row; mat.push(row);
  }));
  const equipmentLines: any[] = [];
  pb.priced.forEach((p) => p.equipment.forEach((r) => equipmentLines.push(r)));
  const laborLines: LaborLine[] = [];
  pb.priced.forEach((p) => p.labor.forEach((l) => { const hit = laborLines.find((x) => x.label === l.label); if (hit) { hit.hours = round2(hit.hours + l.hours); hit.cost = round2(hit.cost + l.cost); } else laborLines.push({ ...l }); }));
  const notes: string[] = [];
  if (pb.missing.length) notes.push(`No rate card yet for ${pb.missing.map((j) => JOB_LABELS[j].toLowerCase()).join(", ")} — set one up under Catalog → Rate cards to price ${pb.missing.length === 1 ? "that line" : "those lines"}.`);
  pb.priced.filter((p) => !p.missingCard && p.needsRate && p.size > 0).forEach((p) => notes.push(`${p.label}: the card has no rate, so this line is $0 until you set one.`));
  const sqft = round2(pb.priced.filter((p) => p.unit === "sq ft").reduce((s2, p) => s2 + p.size, 0));
  return {
    LF: 0, panels: 0, posts: 0, sleeves: 0, concreteBags: 0, adhesiveUnits: 0, gates: 0, corners: 0,
    panelWidth: 0, runCount: 0, closed: false,
    mat, materialTotal: pb.materialTotal, bomExtras: [],
    base: subtotal, markup: null, markupBase: "rate", markupCostBase: 0,
    crew, crewRate, hoursPer300: 0, laborHours: pb.laborHours, laborCost,
    addons: [], addonTotal: 0,
    slope: false, slopeAmount: 0, slopePct: 0, slopeSub: "", gradePct: 0, gradeAmount: 0, measuredGradeSides: 0,
    subtotal, tax, taxRate, taxLabel: s.taxLabel || "Sales tax", customerTotal,
    operatorCost, passThrough: 0, equipmentCost: pb.equipmentCost, extrasCost: extras.total, grossProfit, marginPct,
    notes,
    product: { id: null, name: "Concrete work", panelWidth: 0 },
    concrete: { unit: "sq ft", sqft, blocks: pb.priced, low: pb.low, high: pb.high, missing: pb.missing,
      time: pb.time, timeText: fmtTime(pb.time, headcount), headcount, laborLines, equipment: equipmentLines, extras },
  };
}

/* the quote document's lines — one per block, keyed by job type so the quote
   page's per-line overrides (q.lineOverrides[key]) and descriptions attach */
function concreteLines(result: any) {
  const blocks = (result && result.concrete && result.concrete.blocks) || [];
  return blocks.map((p: PricedBlock) => ({
    key: p.key,
    label: p.where ? `${p.label} — ${p.where}` : p.label,
    sub: p.sub,
    amount: p.amount,
  }));
}

/* what a concrete quote measures: the square feet on it, or the feet of
   caulking when that's the whole job */
function concreteMeasure(result: any) {
  const c = result && result.concrete;
  if (!c) return { qty: 0, unit: "sq ft" };
  if (c.sqft > 0) return { qty: c.sqft, unit: "sq ft" };
  const ft = (c.blocks || []).filter((p: PricedBlock) => p.unit === "ft").reduce((s: number, p: PricedBlock) => s + p.size, 0);
  return { qty: round2(ft), unit: ft > 0 ? "ft" : "sq ft" };
}

/* the head of computeQuoteView (quote.tsx) for a concrete quote: the same
   { product, inputs, fenceQuotes, quote, runGroups, lines } the fence head
   builds, from the rate cards. A first-release typed quote (no blocks, a typed
   total under lineOverrides.base) keeps ONE "base" line so that override still lands. */
function concreteViewHead(q: any, settings: any) {
  const s = settings || {};
  const cards = rateCardsOf(s);
  const concrete = (q && q.inputs && q.inputs.concrete) || {};
  const quote = computeConcreteQuote(concrete, cards, s);
  const legacy = !Array.isArray(concrete.blocks) && q && q.lineOverrides && q.lineOverrides.base != null;
  const lines = legacy
    ? [{ key: "base", label: "Concrete work" + ((q.jobTypes || []).length ? " — " + (q.jobTypes as string[]).map((j) => (JOB_LABELS as any)[j] || j).join(", ").toLowerCase() : ""), sub: (() => { const m = (q.measure && q.measure.qty > 0) ? q.measure : concreteMeasure(quote); return `${fmtQty(num(m.qty))} ${m.unit}`; })(), amount: quote.subtotal }]
    : concreteLines(quote);
  // the card's one-line description fills under each line (the fence head does
  // this per product; computeQuoteView's tail only knows "base", so attach it here)
  lines.forEach((l: any) => {
    const card: any = cards[l.key as JobType] || cards[String(l.key).replace(/-\d+$/, "") as JobType];
    if (card && typeof card.description === "string" && card.description.trim()) l.defaultDesc = card.description.trim();
    if (card && typeof card.warranty === "string" && card.warranty.trim()) l.warranty = card.warranty.trim();
  });
  return { product: quote.product, inputs: q && q.inputs ? q.inputs : {}, fenceQuotes: null, quote, runGroups: [], lines };
}

/* a quote record from the estimator (concrete.tsx) — everything the lists,
   the quote page and the hub read, priced from the cards */
function buildConcreteQuote(form: any, settings: any, extra: any = {}) {
  const blocks: Block[] = blocksOf({ blocks: form.blocks });
  const cards = rateCardsOf(settings);
  const miles = Math.max(0, num(form.miles));
  const result = computeConcreteQuote({ blocks, miles }, cards, settings);
  const m = concreteMeasure(result);
  const jobTypes = blocks.map((b) => b.jobType).filter((j, i, a) => a.indexOf(j) === i);
  return {
    ...extra,
    vertical: "concrete",
    lf: 0, measure: m, jobTypes,
    total: result.customerTotal,
    productName: jobTypes.map((j) => (({ lift: "Lifting & leveling", coat: "Floor coating", clean: "Cleaning", seal: "Sealing", caulk: "Caulking" }) as any)[j]).join(" · ") || "Concrete work",
    productId: null,
    inputs: { linearFeet: 0, gates: 0, manual: true, ...(form.siteVisit ? { siteVisitRequired: true } : {}), concrete: {
      // what a website request carried (photos, the assumptions, the request itself) survives an edit — store.updateQuote replaces `inputs` whole
      ...(form.prev && typeof form.prev === "object" ? ["photos", "assumed", "unsure", "request", "aiScan"].reduce((o: any, k) => (form.prev[k] != null ? { ...o, [k]: form.prev[k] } : o), {}) : {}),
      blocks, surfaces: Array.isArray(form.surfaces) ? form.surfaces : [], notes: form.scopeNotes || "",
      ...(miles > 0 ? { miles: round2(miles) } : {}),   // the round trip the job cost sheet priced (§13 item 22)
      // the slabs drawn on the satellite view and the site they sit on (lib/slabs.ts; plan §5.1) — kept under `concrete`, never as inputs.parcel
      ...(Array.isArray(form.slabs) && form.slabs.length ? { slabs: form.slabs } : {}),
      ...(form.site && typeof form.site === "object" && (form.site.center || form.site.house) ? { site: form.site } : {}),
    } },
    result,
  };
}

export type { JobType, RateCard, Block, PricedBlock, CoatSystem, Component, Per, Kind, When, SupplyLine, LaborLine, JobExtras };
export { stemSqftOf, jobExtrasOf, JOB_TYPES, JOB_LABELS, SURFACES, DROPS, CONDITIONS, COAT_SYSTEMS, PERS, KINDS, WHENS, seedRateCards, rateCardsOf, componentsOf, systemComponentsOf, cardWarnings, blocksOf, liftRateFor, LIFT_METHODS, liftMethodOf, liftUnitRate, liftVoidCuFt, liftFoamLb, cleaningPrice, measuresOf, takeoff, priceBlocks, crewRateOf, computeConcreteQuote, concreteLines, concreteMeasure, concreteViewHead, buildConcreteQuote, fmtInches, fmtTime };
